/**
 * Service tokens (doc 07).
 *
 * Arranged by *integration*, not by credential. `/api/assigned-users` needs a
 * Basic login and a service JWT; presenting them as two independent cards, each
 * with its own "test connection", implied either could be verified alone. It
 * cannot — testing one while the other is missing fails in a way that reads like
 * a bad credential. A stepper per integration makes the pairing, the order and
 * the remaining work obvious.
 *
 * Write-only: the API never returns a stored secret, so a saved slot shows a
 * fingerprint rather than a masked preview. No `••••abcd` tails — the tail of a
 * JWT is signature bytes, but the tail of a Basic string is the end of a
 * password.
 */
import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Snackbar,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';

import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';

import { useCredentialGroups, useSaveCredential, useTestCredential } from '@/api/hooks';
import { ErrorState, PageHeader } from '@/components/states';
import { useEnvironment } from '@/environment/EnvironmentProvider';
import { ENVIRONMENT_STYLE } from '@/environment/store';
import { formatDateTime } from '@/features/license-assignment/cellState';
import type { CredentialGroup, CredentialSlot } from '@/types/api';

const EXPIRY_WARNING_DAYS = 30;

function expiryChip(slot: CredentialSlot) {
  if (!slot.expiresAt) return null;
  const days = Math.ceil((Date.parse(slot.expiresAt) - Date.now()) / 86_400_000);
  if (Number.isNaN(days)) return null;
  if (days < 0) return <Chip label="abgelaufen" color="error" variant="outlined" />;
  if (days <= EXPIRY_WARNING_DAYS)
    return <Chip label={`läuft in ${days} Tagen ab`} color="warning" variant="outlined" />;
  return <Chip label={`gültig bis ${formatDateTime(slot.expiresAt)}`} variant="outlined" />;
}

function SlotStep({
  slot,
  disabled,
  onSaved,
}: {
  slot: CredentialSlot;
  disabled: boolean;
  onSaved: (message: string) => void;
}) {
  // A stored secret is never sent back, so the field always starts empty:
  // replacing a token means pasting a new one.
  const [secret, setSecret] = useState('');
  const [editing, setEditing] = useState(!slot.configured);
  const save = useSaveCredential();

  return (
    <Stack spacing={1.5} sx={{ pb: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {slot.description}
      </Typography>

      {slot.configured && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip label={`Fingerprint ${slot.fingerprint}`} variant="outlined" />
          {slot.source === 'environment' && (
            <Chip label="aus der Umgebung" variant="outlined" color="info" />
          )}
          {expiryChip(slot)}
        </Stack>
      )}

      {slot.lastError && !slot.configured && <Alert severity="warning">{slot.lastError}</Alert>}

      {editing && !disabled && (
        <TextField
          label="Wert einfügen"
          value={secret}
          onChange={(event) => setSecret(event.target.value)}
          multiline
          minRows={2}
          fullWidth
          placeholder="Wird verschlüsselt gespeichert und nie wieder angezeigt."
          // The server strips these for us, so say so — otherwise everyone
          // wonders which half to cut off.
          helperText="Präfixe wie «Basic », «Bearer » oder «webhost_token=» dürfen mitkopiert werden."
        />
      )}

      <Stack direction="row" spacing={1}>
        {editing && !disabled ? (
          <>
            <Button
              variant="contained"
              size="small"
              disabled={!secret.trim() || save.isPending}
              onClick={() =>
                save.mutate(
                  { key: slot.key, secret: secret.trim() },
                  {
                    onSuccess: () => {
                      setSecret('');
                      setEditing(false);
                      onSaved(`${slot.label} gespeichert.`);
                    },
                    onError: (error) => onSaved(error.message),
                  },
                )
              }
            >
              Speichern
            </Button>
            {slot.configured && (
              <Button size="small" onClick={() => setEditing(false)}>
                Abbrechen
              </Button>
            )}
          </>
        ) : (
          !disabled && (
            <Button variant="outlined" size="small" onClick={() => setEditing(true)}>
              Ersetzen
            </Button>
          )
        )}
      </Stack>
    </Stack>
  );
}

function GroupCard({ group }: { group: CredentialGroup }) {
  const [feedback, setFeedback] = useState<string | null>(null);
  const test = useTestCredential();

  // The active step is the first thing still to do: an unconfigured slot, or
  // the connection check once every slot is filled.
  const firstIncomplete = group.slots.findIndex((slot) => !slot.configured);
  const activeStep = firstIncomplete === -1 ? group.slots.length : firstIncomplete;

  const runTest = () => {
    if (!group.testSlot) return;
    test.mutate(group.testSlot, {
      onSuccess: (result) =>
        setFeedback(
          result.ok
            ? `Verbindung erfolgreich. ${result.detail ?? ''}`
            : `Verbindung fehlgeschlagen: ${result.detail ?? ''}`,
        ),
      onError: (error) => setFeedback(error.message),
    });
  };

  const lastResult = group.slots.find((slot) => slot.lastStatus !== null);

  return (
    <Card>
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="h3" component="h3">
                {group.label}
              </Typography>
              {group.complete && (
                <Chip
                  icon={<CheckCircleOutlineIcon />}
                  label="vollständig"
                  color="success"
                  variant="outlined"
                />
              )}
              {!group.enabled && <Chip label="bald" variant="outlined" />}
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {group.description}
            </Typography>
          </Box>

          {!group.enabled ? null : (
            <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
              {group.slots.map((slot) => (
                <Step key={slot.key} completed={slot.configured}>
                  <StepLabel optional={slot.configured ? undefined : 'noch offen'}>
                    {slot.label}
                  </StepLabel>
                  <StepContent>
                    <SlotStep slot={slot} disabled={false} onSaved={setFeedback} />
                  </StepContent>
                </Step>
              ))}

              <Step completed={Boolean(lastResult && (lastResult.lastStatus ?? 500) < 400)}>
                <StepLabel
                  optional={
                    group.complete ? undefined : `zuerst ${group.slots.length} Werte hinterlegen`
                  }
                >
                  Verbindung prüfen
                </StepLabel>
                <StepContent>
                  <Stack spacing={1.5} sx={{ pb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Führt einen echten, harmlosen Aufruf aus — damit ein falscher Wert
                      jetzt auffällt und nicht erst nachts beim Synchronisieren.
                    </Typography>
                    {lastResult?.lastError && <Alert severity="error">{lastResult.lastError}</Alert>}
                    {lastResult?.lastUsedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Zuletzt geprüft: {formatDateTime(lastResult.lastUsedAt)}
                        {lastResult.lastStatus !== null && ` · Status ${lastResult.lastStatus}`}
                      </Typography>
                    )}
                    <Box>
                      <Button
                        variant="contained"
                        size="small"
                        // Gated on completeness: testing a half-filled group
                        // fails in a way that reads like a bad credential.
                        disabled={!group.complete || !group.testSlot || test.isPending}
                        onClick={runTest}
                      >
                        {test.isPending ? 'Prüft…' : 'Verbindung testen'}
                      </Button>
                    </Box>
                  </Stack>
                </StepContent>
              </Step>
            </Stepper>
          )}
        </Stack>
      </CardContent>

      <Snackbar
        open={feedback !== null}
        autoHideDuration={8000}
        onClose={() => setFeedback(null)}
        message={feedback}
      />
    </Card>
  );
}

export function CredentialsPage() {
  const environment = useEnvironment();
  const { data, isPending, error, refetch } = useCredentialGroups();

  return (
    <Box sx={{ maxWidth: 760, width: '100%' }}>
      <PageHeader
        title="Service-Tokens"
        description="Zugangsdaten, die das Backend für Aufrufe an andere SEP-Dienste verwendet. Sie werden verschlüsselt gespeichert und nie wieder angezeigt."
      />
      {/* Named explicitly. Every environment has its own set, and the form looks
          identical in all three — without this the only clue is the switcher in
          the app bar, which is a long way from the paste box. Pasting
          production's token into staging's slot is a mistake worth one line of
          prevention. */}
      <Alert severity="info" icon={<KeyOutlinedIcon />} sx={{ mb: 2 }}>
        Diese Zugangsdaten gelten für die Umgebung{' '}
        <strong>{ENVIRONMENT_STYLE[environment].label}</strong>. Jede Umgebung hat
        ihre eigenen Tokens — ein Wechsel oben in der Leiste zeigt einen anderen
        Satz.
      </Alert>
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      <Stack spacing={2}>
        {isPending
          ? Array.from({ length: 3 }, (_, index) => (
              <Skeleton key={index} variant="rectangular" height={220} />
            ))
          : data?.map((group) => <GroupCard key={group.key} group={group} />)}
      </Stack>
    </Box>
  );
}
